import type { Circuit, JokerLapBranch, PlayerTrackPosition, TrackCell } from '../types/track.js';
import { TrackCellType } from '../types/track.js';
import type { ValidationResult } from '../types/track.js';
import { areCellsAdjacent } from './circuitGeometryEdit.js';
import { hasStartLane } from './startLane.js';

export function hasJokerLap(circuit: Circuit): boolean {
  return (circuit.jokerLap?.cells.length ?? 0) > 0;
}

/** Tour joker obligatoire une fois par course (multi-tours avec branche définie). */
export function isJokerLapMandatoryForRace(circuit: Circuit, numLaps: number): boolean {
  return numLaps > 1 && hasJokerLap(circuit);
}

/** Dernier tour : le tour joker doit être emprunté s'il ne l'a pas encore été. */
export function mustTakeJokerLapNow(
  circuit: Circuit,
  numLaps: number,
  completedLaps: number,
  jokerLapUsed: boolean
): boolean {
  return (
    !jokerLapUsed && isJokerLapMandatoryForRace(circuit, numLaps) && completedLaps >= numLaps - 1
  );
}

/** Choix effectif à l'entrée (force le joker au dernier tour). */
export function resolveEffectiveJokerLapEntryChoice(
  circuit: Circuit,
  numLaps: number,
  completedLaps: number,
  jokerLapUsed: boolean,
  explicitChoice?: boolean | null
): boolean | null {
  if (mustTakeJokerLapNow(circuit, numLaps, completedLaps, jokerLapUsed)) return true;
  return explicitChoice ?? null;
}

/** Un pilote peut terminer seulement s'il a complété les tours ET pris le tour joker si requis. */
export function canFinishRace(
  circuit: Circuit,
  numLaps: number,
  completedLaps: number,
  jokerLapUsed: boolean
): boolean {
  if (completedLaps < numLaps) return false;
  if (!isJokerLapMandatoryForRace(circuit, numLaps)) return true;
  return jokerLapUsed;
}

export function createEmptyJokerLapBranch(): JokerLapBranch {
  return {
    entryPathIndex: -1,
    exitPathIndex: -1,
    cells: [],
  };
}

export function mainPathLength(circuit: Circuit): number {
  return circuit.path.length;
}

export function normalizeMainPathIndex(index: number, pathLen: number): number {
  if (pathLen <= 0) return 0;
  return ((index % pathLen) + pathLen) % pathLen;
}

/**
 * Cases restantes jusqu’à la sortie du tour joker (0 = sur la case de sortie).
 * Retourne null si le pilote est hors zone (avant l’entrée ou après la sortie sur le path).
 */
export function cellsToJokerExit(circuit: Circuit, position: PlayerTrackPosition): number | null {
  const jl = circuit.jokerLap;
  if (!jl || jl.cells.length === 0 || jl.entryPathIndex < 0 || jl.exitPathIndex < 0) {
    return null;
  }
  if (position.startLaneIndex != null) return null;

  const pathLen = circuit.path.length;
  if (pathLen === 0) return null;

  const entry = normalizeMainPathIndex(jl.entryPathIndex, pathLen);
  const exit = normalizeMainPathIndex(jl.exitPathIndex, pathLen);

  if (position.jokerBranchIndex != null) {
    return Math.max(0, jl.cells.length - 1 - position.jokerBranchIndex);
  }

  const mainIdx = normalizeMainPathIndex(position.mainPathIndex, pathLen);
  const forwardSpan = (exit - entry + pathLen) % pathLen;
  if (forwardSpan === 0) return null;

  const fromEntry = (mainIdx - entry + pathLen) % pathLen;
  if (fromEntry <= forwardSpan) {
    return (exit - mainIdx + pathLen) % pathLen;
  }
  return null;
}

export interface StepTrackPositionOptions {
  /** À l'entrée du tour joker : true = branche, false = circuit principal. */
  enterJokerLap?: boolean;
}

export function isOnJokerLapEntry(circuit: Circuit, position: PlayerTrackPosition): boolean {
  const jl = circuit.jokerLap;
  if (!jl || jl.entryPathIndex < 0 || jl.cells.length === 0) return false;
  if (position.jokerBranchIndex != null) return false;
  if (position.startLaneIndex != null) return false;
  const pathLen = circuit.path.length;
  if (pathLen === 0) return false;
  return normalizeMainPathIndex(position.mainPathIndex, pathLen) === jl.entryPathIndex;
}

/** Choix requis à l'entrée (course multi-tours, tour joker encore disponible). */
export function needsJokerLapEntryChoice(
  circuit: Circuit,
  position: PlayerTrackPosition,
  jokerLapUsed: boolean,
  numLaps: number
): boolean {
  return (
    numLaps > 1 && hasJokerLap(circuit) && !jokerLapUsed && isOnJokerLapEntry(circuit, position)
  );
}

/** Résout emprunt de la branche à l'entrée (mono-tour : branche par défaut). */
export function resolveEnterJokerLapAtEntry(
  circuit: Circuit,
  position: PlayerTrackPosition,
  jokerLapUsed: boolean,
  numLaps: number,
  explicitChoice?: boolean | null,
  completedLaps?: number
): boolean {
  if (jokerLapUsed || !isOnJokerLapEntry(circuit, position)) return false;
  if (numLaps <= 1) return true;
  if (completedLaps != null && mustTakeJokerLapNow(circuit, numLaps, completedLaps, jokerLapUsed)) {
    return true;
  }
  return explicitChoice === true;
}

/**
 * Choix bot à l'entrée du tour joker : aléatoire, sauf obligation au dernier tour
 * si le tour joker n'a pas encore été emprunté (une seule fois par course).
 */
export function resolveBotJokerLapEntryChoice(
  jokerLapUsed: boolean,
  completedLaps: number,
  numLaps: number,
  random: () => number = Math.random,
  circuit?: Circuit
): boolean {
  if (jokerLapUsed || numLaps <= 1) return false;
  if (circuit && mustTakeJokerLapNow(circuit, numLaps, completedLaps, jokerLapUsed)) return true;
  if (!circuit && completedLaps >= numLaps - 1) return true;
  return random() < 0.5;
}

export function getPlayerTrackCell(
  circuit: Circuit,
  position: PlayerTrackPosition
): TrackCell | null {
  const sl = circuit.startLane;
  if (position.startLaneIndex != null && sl?.cells.length) {
    return sl.cells[position.startLaneIndex] ?? null;
  }
  const jl = circuit.jokerLap;
  if (position.jokerBranchIndex != null && jl?.cells.length) {
    return jl.cells[position.jokerBranchIndex] ?? null;
  }
  const len = circuit.path.length;
  if (len === 0) return null;
  return circuit.path[normalizeMainPathIndex(position.mainPathIndex, len)] ?? null;
}

/**
 * Avance d’une case selon les règles du tour joker.
 * Marque `jokerLapUsed` à true quand le pilote atteint la dernière case du tour joker.
 */
export function stepTrackPosition(
  circuit: Circuit,
  position: PlayerTrackPosition,
  jokerLapUsed: boolean,
  options?: StepTrackPositionOptions
): { position: PlayerTrackPosition; jokerLapUsed: boolean } {
  const pathLen = circuit.path.length;
  if (pathLen === 0) return { position, jokerLapUsed };

  const sl = circuit.startLane;
  if (position.startLaneIndex != null && hasStartLane(circuit) && sl) {
    const laneIdx = position.startLaneIndex;
    if (laneIdx > 0) {
      return {
        position: {
          mainPathIndex: sl.joinPathIndex,
          jokerBranchIndex: null,
          startLaneIndex: laneIdx - 1,
        },
        jokerLapUsed,
      };
    }
    return {
      position: {
        mainPathIndex: normalizeMainPathIndex(sl.joinPathIndex, pathLen),
        jokerBranchIndex: null,
        startLaneIndex: null,
      },
      jokerLapUsed,
    };
  }

  const jl = circuit.jokerLap;
  if (!jl || jl.cells.length === 0 || jl.entryPathIndex < 0 || jl.exitPathIndex < 0) {
    return {
      position: {
        mainPathIndex: normalizeMainPathIndex(position.mainPathIndex + 1, pathLen),
        jokerBranchIndex: null,
        startLaneIndex: null,
      },
      jokerLapUsed,
    };
  }

  if (position.jokerBranchIndex != null) {
    const branchIdx = position.jokerBranchIndex;
    if (branchIdx < jl.cells.length - 1) {
      const nextBranchIdx = branchIdx + 1;
      return {
        position: {
          mainPathIndex: position.mainPathIndex,
          jokerBranchIndex: nextBranchIdx,
          startLaneIndex: null,
        },
        jokerLapUsed: nextBranchIdx === jl.cells.length - 1 ? true : jokerLapUsed,
      };
    }
    return {
      position: {
        mainPathIndex: normalizeMainPathIndex(jl.exitPathIndex, pathLen),
        jokerBranchIndex: null,
        startLaneIndex: null,
      },
      jokerLapUsed: true,
    };
  }

  const mainIdx = normalizeMainPathIndex(position.mainPathIndex, pathLen);
  if (mainIdx === jl.entryPathIndex) {
    if (!jokerLapUsed && options?.enterJokerLap === true) {
      return {
        position: { mainPathIndex: mainIdx, jokerBranchIndex: 0, startLaneIndex: null },
        jokerLapUsed,
      };
    }
    return {
      position: {
        mainPathIndex: normalizeMainPathIndex(mainIdx + 1, pathLen),
        jokerBranchIndex: null,
        startLaneIndex: null,
      },
      jokerLapUsed,
    };
  }

  return {
    position: {
      mainPathIndex: normalizeMainPathIndex(mainIdx + 1, pathLen),
      jokerBranchIndex: null,
      startLaneIndex: null,
    },
    jokerLapUsed,
  };
}

/** Avance de `steps` cases (retourne position finale et si le tour joker a été consommé). */
export function advanceTrackPosition(
  circuit: Circuit,
  position: PlayerTrackPosition,
  steps: number,
  jokerLapUsed: boolean,
  options?: StepTrackPositionOptions & {
    numLaps?: number;
    enterJokerLapChoice?: boolean | null;
    completedLaps?: number;
  }
): { position: PlayerTrackPosition; jokerLapUsed: boolean; stepsTaken: number } {
  let pos = position;
  let used = jokerLapUsed;
  let taken = 0;
  const numLaps = options?.numLaps ?? 1;
  const completedLaps = options?.completedLaps ?? 0;
  for (let i = 0; i < steps; i++) {
    const effective = resolveEffectiveJokerLapEntryChoice(
      circuit,
      numLaps,
      completedLaps,
      used,
      options?.enterJokerLapChoice ?? null
    );
    const enterJokerLap = resolveEnterJokerLapAtEntry(
      circuit,
      pos,
      used,
      numLaps,
      effective,
      completedLaps
    );
    const next = stepTrackPosition(circuit, pos, used, { enterJokerLap });
    pos = next.position;
    used = next.jokerLapUsed;
    taken++;
  }
  return { position: pos, jokerLapUsed: used, stepsTaken: taken };
}

export function validateJokerLap(circuit: Circuit): ValidationResult {
  const errors: string[] = [];
  const jl = circuit.jokerLap;
  if (!jl || (jl.entryPathIndex < 0 && jl.cells.length === 0 && jl.exitPathIndex < 0)) {
    return { isValid: true, errors: [] };
  }

  const pathLen = circuit.path.length;
  if (pathLen === 0) {
    errors.push('Le circuit principal doit exister avant d’ajouter un tour joker');
    return { isValid: false, errors };
  }

  if (jl.cells.length === 0) {
    errors.push('Le tour joker doit contenir au moins une case');
  }

  if (jl.entryPathIndex < 0 || jl.entryPathIndex >= pathLen) {
    errors.push('Point d’entrée du tour joker invalide');
  }
  if (jl.exitPathIndex < 0 || jl.exitPathIndex >= pathLen) {
    errors.push('Point de sortie du tour joker invalide');
  }
  if (jl.entryPathIndex >= 0 && jl.exitPathIndex >= 0 && jl.entryPathIndex === jl.exitPathIndex) {
    errors.push('L’entrée et la sortie du tour joker doivent être distinctes');
  }

  if (jl.entryPathIndex >= 0 && jl.cells.length > 0) {
    const entry = circuit.path[jl.entryPathIndex];
    const first = jl.cells[0];
    if (!areCellsAdjacent(entry.x, entry.y, first.x, first.y)) {
      errors.push('La première case du tour joker doit être adjacente à l’entrée');
    }
  }

  for (let i = 1; i < jl.cells.length; i++) {
    const prev = jl.cells[i - 1];
    const curr = jl.cells[i];
    if (!areCellsAdjacent(prev.x, prev.y, curr.x, curr.y)) {
      errors.push('Les cases du tour joker doivent être adjacentes entre elles');
      break;
    }
  }

  if (jl.exitPathIndex >= 0 && jl.cells.length > 0) {
    const exit = circuit.path[jl.exitPathIndex];
    const last = jl.cells[jl.cells.length - 1];
    if (!areCellsAdjacent(last.x, last.y, exit.x, exit.y)) {
      errors.push('La dernière case du tour joker doit être adjacente à la sortie');
    }
  }

  for (const cell of jl.cells) {
    if (cell.type !== TrackCellType.JOKER_LAP) {
      errors.push('Toutes les cases du tour joker doivent être de type tour joker');
      break;
    }
  }

  return { isValid: errors.length === 0, errors };
}

/** Fusionne le path principal et la branche joker dans la grille. */
export function applyJokerLapToGrid(circuit: Circuit): TrackCellType[][] {
  const grid = circuit.grid.map((row) => [...row]);
  const cells = circuit.jokerLap?.cells ?? [];
  for (const cell of cells) {
    if (cell.y >= 0 && cell.y < circuit.height && cell.x >= 0 && cell.x < circuit.width) {
      grid[cell.y][cell.x] = TrackCellType.JOKER_LAP;
    }
  }
  return grid;
}
